import { motion } from "framer-motion";
import { CalendarCheckIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";

import { Card } from "@/components/card";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { TabNavigation } from "@/components/ui/tab-navigation";
import UploadInput from "@/features/file-upload/components/upload-input";
import { useOrganisation } from "@/features/select-organisation/api/get-my-organisation";
import { OptionalSettingsElement } from "@/features/session/components/optional-settings.element";
import { SessionDetailsElement } from "@/features/session/components/session-details.element";
import { queryClient } from "@/lib/react-query";
import { PublicEvent, SessionInvitesStatus } from "@classview/shared-types";
import { zodResolver } from "@hookform/resolvers/zod";

import { convertApiDataToFormData } from "../api/convert-api-data-to-form-data";
import {
  convertFormDataToApiData,
  convertFormDataToUpdateApiData,
} from "../api/convert-form-data-to-api-data";
import { useCreateEvent } from "../api/create-event";
import { useUpdateEvent } from "../api/update-event";
import {
  EventSchema,
  EventVisibility,
  eventSchema,
} from "../types/manage-event.schema";

import { ManageEventMetadataElement } from "./manage-event-metadata.element";

type ManageEventFormProps = {
  initialValues?: PublicEvent;
};

export function ManageEventForm({ initialValues }: ManageEventFormProps) {
  const isEdit = !!initialValues;
  const { organisation } = useOrganisation();
  const navigate = useNavigate();

  const form = useForm<EventSchema>({
    resolver: zodResolver(eventSchema),
    defaultValues: convertApiDataToFormData(initialValues),
  });

  const { mutateAsync: createEvent, isPending: isCreating } = useCreateEvent({
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      navigate(`/events/${result.id}/edit-attendees`);
    },
  });

  async function handleCreateEvent(data: EventSchema) {
    await createEvent(convertFormDataToApiData(organisation?.id, data));
  }

  const { mutateAsync: updateEvent, isPending: isUpdating } = useUpdateEvent({
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["events"] });
      navigate(`/events/${result.id}`);
      form.reset(convertApiDataToFormData(result));
    },
  });

  function handleUpdateEvent(data: EventSchema) {
    updateEvent({
      eventId: initialValues?.id ?? "",
      orgId: organisation?.id ?? "",
      event: convertFormDataToUpdateApiData(data),
    });
  }

  const isCapacityCapped = form.watch("isCapacityCapped");

  const onSubmitFunction = isEdit ? handleUpdateEvent : handleCreateEvent;
  const isDirty = form.formState.isDirty;

  const invitedOrganisations =
    initialValues?.session.invitedOrganisations || [];
  const invitedOrganisationNames: string[] = invitedOrganisations
    .filter(
      (org) =>
        org.status === SessionInvitesStatus.ACCEPTED ||
        org.status === SessionInvitesStatus.HOST,
    )
    .map((org) => org.name);
  return (
    <>
      {isEdit && (
        <TabNavigation
          options={[
            {
              label: "Event Details",
              to: `/events/${initialValues?.id}/edit`,
            },
            {
              label: "Manage Attendees",
              to: `/events/${initialValues?.id}/edit-attendees`,
            },
          ]}
        />
      )}
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmitFunction)}
          className="space-y-4"
        >
          <Card>
            <Card.Header
              title="Event Details"
              description="Configure your event"
            />
            <Card.Content className="space-y-4">
              <SessionDetailsElement isDescriptionRequired={true} />
              <div>
                <Separator className="my-6" />
                <FormField
                  control={form.control}
                  name="bannerS3Path"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isRequired>Banner</FormLabel>
                      <FormDescription>
                        You can upload .jpeg, .png, .gif, or .webp files.
                      </FormDescription>
                      <FormControl>
                        <UploadInput
                          name="bannerS3Path"
                          onChange={field.onChange}
                          value={field.value}
                          maxSizeInMB={5}
                          allowedFileTypes={[
                            "image/jpeg",
                            "image/png",
                            "image/gif",
                            "image/webp",
                          ]}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <Separator className="my-6" />
                <FormField
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel isRequired>Visibility</FormLabel>

                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={EventVisibility.PRIVATE} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Private
                              </FormLabel>
                              <FormDescription>
                                Only invited attendees from{" "}
                                <span className="italic">
                                  {invitedOrganisationNames.join(", ")}
                                </span>{" "}
                                organisation
                                {invitedOrganisationNames.length > 1
                                  ? "s"
                                  : ""}{" "}
                                can see this event
                              </FormDescription>
                            </div>
                          </FormItem>
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem
                                value={EventVisibility.ORGANISATION_WIDE}
                              />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Organisation wide
                              </FormLabel>
                              <FormDescription>
                                Everyone within{" "}
                                <span className="italic">
                                  {invitedOrganisationNames.join(", ")}
                                </span>{" "}
                                organisation
                                {invitedOrganisationNames.length > 1
                                  ? "s"
                                  : ""}{" "}
                                will be able to see this event
                              </FormDescription>
                            </div>
                          </FormItem>
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={EventVisibility.PUBLIC} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Public
                              </FormLabel>
                              <FormDescription>
                                Everyone will be able to see this event
                              </FormDescription>
                            </div>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div>
                <Separator className="my-6" />
                <ManageEventMetadataElement />
              </div>

              {/* <div>
                <Separator className="my-6" />
                <FormLabel>Event Cost</FormLabel>
                <FormField
                  control={form.control}
                  name="isFree"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value ? "true" : "false"}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={"false"} disabled={true} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Paid Event
                              </FormLabel>
                              <FormDescription>
                                Users must pay to register for this event
                              </FormDescription>
                            </div>
                          </FormItem>
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={"true"} disabled={false} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Free Event
                              </FormLabel>
                              <FormDescription>
                                Users can attend the event for free
                              </FormDescription>
                            </div>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div> */}
              {/* <div>
                <Separator className="my-6" />
                <FormLabel>Registration</FormLabel>
                <FormField
                  control={form.control}
                  name="isApprovalRequired"
                  render={({ field }) => (
                    <FormItem className="mt-4">
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value ? "true" : "false"}
                          className="flex flex-col space-y-1"
                        >
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={"false"} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Self Register
                              </FormLabel>
                              <FormDescription>
                                Users are able to register for the event without
                                approval
                              </FormDescription>
                            </div>
                          </FormItem>
                          <FormItem className="flex space-x-3 space-y-0">
                            <FormControl>
                              <RadioGroupItem value={"true"} disabled={true} />
                            </FormControl>
                            <div className="space-y-1 leading-none">
                              <FormLabel className="font-normal">
                                Approval Required
                              </FormLabel>
                              <FormDescription>
                                Users can request to register to the event,
                                event managers will need to approve
                                registrations before they are added to the
                                event.
                              </FormDescription>
                            </div>
                          </FormItem>
                        </RadioGroup>
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div> */}
              <div>
                <Separator className="my-6" />
                <FormLabel>Limit Spaces</FormLabel>
                <FormField
                  control={form.control}
                  name="isCapacityCapped"
                  render={({ field }) => (
                    <FormItem>
                      <div className="flex flex-row items-center">
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="ml-2 mt-0">
                          <FormLabel className="text-base">
                            Does your event have limited capacity?
                          </FormLabel>
                          <FormDescription>
                            For example, we'd suggest workshops are capped at 10
                            participants.
                          </FormDescription>
                        </div>
                      </div>
                    </FormItem>
                  )}
                />
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={
                    isCapacityCapped
                      ? { opacity: 1, height: "auto" }
                      : { opacity: 0, height: 0 }
                  }
                  transition={{ duration: 0.3 }}
                >
                  <FormField
                    control={form.control}
                    name="capacityLimit"
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>Spaces Capped at</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            min={1}
                            {...field}
                            onChange={(event) => {
                              const value = event.target.value;
                              if (!value) {
                                field.onChange(event);
                                return;
                              }

                              form.setValue("capacityLimit", parseInt(value));
                            }}
                          />
                        </FormControl>
                        <FormDescription>
                          If you exceed your organisations concurrency limit you
                          may face additional charges.
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </motion.div>
              </div>
            </Card.Content>
          </Card>
          <Card>
            <Card.Header
              title="Suggested Settings"
              description="Based on the event type"
            />
            <Card.Content>
              <OptionalSettingsElement />
            </Card.Content>
          </Card>
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={
              isDirty
                ? { opacity: 1, height: "auto" }
                : { opacity: 0, height: 0 }
            }
            transition={{ duration: 0.3 }}
          >
            <div className="flex items-center justify-end gap-4">
              {isEdit ? (
                <Button
                  type="submit"
                  isLoading={form.formState.isSubmitting || isUpdating}
                  isAnimated
                >
                  Update Event
                </Button>
              ) : (
                <>
                  <Button variant="ghost" asChild>
                    <Link to="/events/discover">Cancel</Link>
                  </Button>
                  <Button
                    type="submit"
                    isLoading={form.formState.isSubmitting || isCreating}
                    isAnimated
                  >
                    <CalendarCheckIcon className="mr-2 h-4 w-4" />
                    Create Event
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        </form>
      </Form>
    </>
  );
}
